/**
 * PipeTorqueFields.tsx
 * FieldIQ — Casing Running / Pipe Profile Form
 *
 * Drop this into your existing pipe profile form wherever Size, Weight,
 * Grade, and Connection Type are already collected. It handles:
 *   - Auto-populating torque fields from API spec (LTC/STC/BTC)
 *   - Recalling saved torque profiles for premium connections
 *   - Showing shoulder torque fields only when relevant
 *   - Saving new combos to DB on form submit
 *
 * Usage:
 *   <PipeTorqueFields
 *     size={formData.casingSize}
 *     weightPpf={formData.casingWeight}
 *     grade={formData.casingGrade}
 *     connectionType={formData.connectionType}
 *     onChange={(torqueFields) => setFormData(prev => ({ ...prev, ...torqueFields }))}
 *   />
 */

import React, { useEffect, useState } from 'react';
import {
  useTorqueProfile,
  toTorqueProfileGradeKey,
  isKnownShoulderedConnection,
  CONNECTION_OPTIONS,
  type ConnectionType,
  type CasingGrade,
  type TorqueProfile,
} from './useTorqueProfile';
import { CASING_SIZES } from '../../data/casingData';
import { getApiErrorMessage } from '../../api/client';
import { torqueProfileExistsOf } from '../../lib/torque-profile-exists';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TorqueFieldValues {
  torqueMin: number | null;
  torqueOptimum: number | null;
  torqueMax: number | null;
  torqueLowShoulder: number | null;
  torqueHighShoulder: number | null;
  torqueSource: string;
}

interface PipeTorqueFieldsProps {
  // Inputs (controlled by parent form)
  size: string;
  weightPpf: number | null;
  grade: CasingGrade | '';
  connectionType: ConnectionType | '';
  /** Called whenever torque values change — merge into your form state */
  onChange: (values: TorqueFieldValues) => void;
  /** Notify parent that a new profile was saved (optional) */
  onProfileSaved?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v: number | null) => (v !== null ? v.toLocaleString() : '');

const CASING_SIZE_LABELS = CASING_SIZES.map(s => s.label);

const GRADES: CasingGrade[] = [
  'H-40', 'J-55', 'K-55', 'SOO55', 'M-65', 'N-80', 'L-80', '13Cr L-80', 'PS-80', 'TN 80SS', 'C-90', 'T-95', 'P-110', 'Q-125',
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  section: {
    borderTop: '1px solid rgba(55,65,81,0.5)',
    paddingTop: '20px',
    marginTop: '20px',
  } as React.CSSProperties,

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'hsl(var(--foreground))',
    margin: 0,
    letterSpacing: '0.01em',
  } as React.CSSProperties,

  badge: (variant: 'spec' | 'recalled' | 'empty' | 'loading' | 'error') => {
    const map = {
      spec:     { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: 'rgba(245,158,11,0.3)', label: 'API RP 5C1' },
      recalled: { bg: 'rgba(16,185,129,0.15)',   color: '#34d399', border: 'rgba(16,185,129,0.3)',   label: 'Saved profile recalled' },
      empty:    { bg: 'rgba(55,65,81,0.3)',   color: '#64748b', border: 'rgba(55,65,81,0.5)',   label: 'Enter torque values' },
      loading:  { bg: 'rgba(55,65,81,0.3)',   color: '#64748b', border: 'rgba(55,65,81,0.5)',   label: 'Looking up…' },
      error:    { bg: 'rgba(239,68,68,0.15)',   color: '#f87171', border: 'rgba(239,68,68,0.3)',   label: 'Lookup failed' },
    };
    const m = map[variant];
    return {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 600,
      background: m.bg,
      color: m.color,
      border: `1px solid ${m.border}`,
      letterSpacing: '0.03em',
    } as React.CSSProperties;
  },

  pipeGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '16px',
  } as React.CSSProperties,

  connFull: {
    marginBottom: '16px',
  } as React.CSSProperties,

  fieldGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '5px',
  } as React.CSSProperties,

  label: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#64748b',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  input: (readonly?: boolean) => ({
    background: readonly ? 'rgba(15,23,42,0.5)' : 'rgba(15,23,42,0.8)',
    border: '1px solid rgba(55,65,81,0.5)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '14px',
    color: '#ffffff',
    width: '100%',
    fontFamily: 'inherit',
    cursor: readonly ? 'default' : 'text',
  }) as React.CSSProperties,

  select: {
    appearance: 'none' as const,
    background: 'rgba(15,23,42,0.8)',
    border: '1px solid rgba(55,65,81,0.5)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '14px',
    color: '#ffffff',
    width: '100%',
    fontFamily: 'inherit',
    cursor: 'pointer',
  } as React.CSSProperties,

  divider: {
    height: '1px',
    background: 'rgba(55,65,81,0.5)',
    margin: '16px 0',
  } as React.CSSProperties,

  torqueGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
    marginBottom: '12px',
  } as React.CSSProperties,

  shoulderGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '12px',
  } as React.CSSProperties,

  readonlyCard: {
    background: 'rgba(15,23,42,0.5)',
    border: '1px solid rgba(55,65,81,0.5)',
    borderRadius: '8px',
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  } as React.CSSProperties,

  readonlyValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#ffffff',
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,

  readonlyValueOpt: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#f59e0b',
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,

  readonlyUnit: {
    fontSize: '11px',
    color: '#94a3b8',
  } as React.CSSProperties,

  readonlyLabel: {
    fontSize: '11px',
    color: '#64748b',
    fontWeight: 500,
    marginTop: '2px',
  } as React.CSSProperties,

  saveBtn: (canSave: boolean) => ({
    background: canSave ? '#d4943a' : 'rgba(55,65,81,0.3)',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 18px',
    fontSize: '13px',
    fontWeight: 600,
    color: canSave ? '#1a1a2e' : '#94a3b8',
    cursor: canSave ? 'pointer' : 'not-allowed',
    fontFamily: 'inherit',
    transition: 'background 0.15s',
  }) as React.CSSProperties,

  hint: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
    lineHeight: 1.5,
  } as React.CSSProperties,

  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(245,158,11,0.1)',
    border: '1px solid rgba(245,158,11,0.25)',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#fbbf24',
    marginBottom: '12px',
  } as React.CSSProperties,

  // Save rejected by the server. Red, not amber — amber is the advisory
  // infoRow, and "your torque profile is not stored" is not advisory. Stays on
  // screen until the user edits a value or changes the pipe combination.
  errorRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.35)',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#fca5a5',
    marginTop: '10px',
    lineHeight: 1.5,
  } as React.CSSProperties,

  // Replacing a published figure is not advisory, so it is not the amber
  // infoRow. Distinct from errorRow (red) because nothing has gone wrong — the
  // user is about to do something deliberate and consequential.
  warnRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '10px 12px',
    background: 'rgba(234,179,8,0.12)',
    border: '1px solid rgba(234,179,8,0.45)',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#eab308',
    marginBottom: '12px',
    lineHeight: 1.5,
  } as React.CSSProperties,

  // An action rendered inline in a sentence. A real <button> rather than a
  // styled span so it is reachable by keyboard and announced as an action.
  linkBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 600,
  } as React.CSSProperties,

  sourceSelector: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '14px',
  } as React.CSSProperties,

  sourceOption: (selected: boolean) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    padding: '10px 14px',
    borderRadius: '8px',
    border: selected
      ? '2px solid #d4943a'
      : '1px solid rgba(55,65,81,0.5)',
    background: selected
      ? 'rgba(212,148,58,0.1)'
      : 'rgba(15,23,42,0.4)',
    cursor: 'pointer',
    transition: 'border 0.15s, background 0.15s',
  }) as React.CSSProperties,

  sourceLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  } as React.CSSProperties,

  sourceValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#ffffff',
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,

  sourceUnit: {
    fontSize: '11px',
    fontWeight: 400,
    color: '#94a3b8',
  } as React.CSSProperties,

  sourceCheck: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#d4943a',
    marginTop: '2px',
  } as React.CSSProperties,
};

// ─── Component ────────────────────────────────────────────────────────────────

export const PipeTorqueFields: React.FC<PipeTorqueFieldsProps> = ({
  size,
  weightPpf,
  grade,
  connectionType,
  onChange,
  onProfileSaved,
}) => {
  const {
    torque, status, hasShoulders, isEditable,
    isOverriding, beginOverride, cancelOverride,
    canRevert, saveTorqueProfile, refresh, revertToLibrary,
  } = useTorqueProfile({
    size,
    weightPpf,
    grade,
    connectionType,
  });

  /**
   * The badge text is the API's `sourceLabel`, rendered verbatim — never a
   * string retyped here. The call sheet PDF prints the same field from the same
   * resolver, so the screen and the paper cannot word the provenance
   * differently. An empty label means the resolver found nothing, which the
   * "enter values" state below covers.
   */
  const sourceLabel = torque?.sourceLabel ?? '';
  const isCompanyValue = torque?.source === 'company';
  const isLibraryValue = torque?.source === 'api_spec';
  const isMiss = status === 'resolved' && (torque === null || torque.source === null);

  // Local editable state for premium connections
  const [localValues, setLocalValues] = useState<TorqueFieldValues>({
    torqueMin: null,
    torqueOptimum: null,
    torqueMax: null,
    torqueLowShoulder: null,
    torqueHighShoulder: null,
    torqueSource: '',
  });
  const [savedFeedback, setSavedFeedback] = useState(false);
  // Set only when the server rejected the save. Holds the API's own message.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reverting, setReverting] = useState(false);

  /**
   * The "Shouldered connection" checkbox (api#830). It belongs to the COMBO,
   * like the torques, and is only offered while the fields are open — a new
   * combo, or an override/edit in progress. Seeded from `hasShoulders`: the
   * saved flag for a combo on file, the known-premium list for one that is not.
   * Read-only, the shoulder fields follow the saved flag and this is unused.
   */
  const [shoulderedChoice, setShoulderedChoice] = useState<boolean>(false);
  const showShoulders = isEditable ? shoulderedChoice : hasShoulders;
  // Unticking a combo whose saved flag is on removes its shoulder torques on
  // save — the API stores them only for a shouldered combo. Say so first.
  const dropsSavedShoulders = isEditable && isCompanyValue && torque?.shouldered === true && !shoulderedChoice;

  /**
   * Delete the company override and go back to the published value.
   *
   * Surfaces failure the same way the save does: a revert that did not happen
   * must not look like one that did, or the crew reads a company number while
   * believing it is API RP 5C1.
   */
  const handleRevert = async () => {
    setSaveError(null);
    setReverting(true);
    try {
      await revertToLibrary();
    } catch (err) {
      setSaveError(
        getApiErrorMessage(err, 'The company value was not removed. Check your connection and try again.')
      );
    } finally {
      setReverting(false);
    }
  };

  // Sync torque profile into local state + notify parent.
  //
  // Fires when useTorqueProfile resolves a new torque object or status. The
  // matchesCurrent guard prevents stale values from a PREVIOUS combination
  // leaking into the current one (safety-critical: over-torque risk). Deps
  // are [torque, status] — NOT the four combo fields — so the effect only
  // fires AFTER the hook has resolved the lookup for the current combination,
  // avoiding a premature clear that would race the populate path.
  useEffect(() => {
    const matchesCurrent =
      !!torque &&
      torque.size === size &&
      torque.weightPpf === weightPpf &&
      toTorqueProfileGradeKey(torque.grade) === toTorqueProfileGradeKey(grade as string) &&
      torque.connectionType === connectionType;

    if (!matchesCurrent) {
      const cleared = {
        torqueMin: null, torqueOptimum: null, torqueMax: null,
        torqueLowShoulder: null, torqueHighShoulder: null, torqueSource: '',
      };
      setLocalValues(cleared);
      onChange(cleared);
      setShoulderedChoice(isKnownShoulderedConnection(connectionType));
      setSaveError(null); // different pipe combination — the old failure no longer applies
      return;
    }
    const v: TorqueFieldValues = {
      torqueMin: torque.torqueMin,
      torqueOptimum: torque.torqueOptimum,
      torqueMax: torque.torqueMax,
      torqueLowShoulder: torque.torqueLowShoulder,
      torqueHighShoulder: torque.torqueHighShoulder,
      torqueSource: torque.sourceLabel,
    };
    setLocalValues(v);
    onChange(v);
    setShoulderedChoice(hasShoulders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torque, status]);

  // Cancelling an edit puts the checkbox back to what is on file, along with
  // the fields.
  const handleCancelOverride = () => {
    setShoulderedChoice(hasShoulders);
    cancelOverride();
  };

  const handleLocalChange = (field: keyof TorqueFieldValues, raw: string) => {
    const parsed = raw === '' ? null : parseInt(raw.replace(/,/g, ''), 10);
    const updated = { ...localValues, [field]: isNaN(parsed as number) ? null : parsed };
    setLocalValues(updated);
    onChange(updated);
    setSaveError(null); // the user is acting on the failure; drop the stale message
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await saveTorqueProfile({ ...localValues, shouldered: shoulderedChoice });
    } catch (err) {
      // The field may add a combo but never change one (api#830). Another hand
      // saved this one first: show what is on file rather than a refusal with
      // nothing behind it. Only a field worker can be told this.
      if (torqueProfileExistsOf(err)) {
        setSaveError('Torque for this pipe is already on file — it is shown above. Only the office can change it.');
        try { await refresh(); } catch { /* the message stands on its own */ }
        return;
      }
      // The whole point: tell the crew WHY, so the next attempt is informed.
      // `localValues` is deliberately untouched — the hook updated no state, so
      // the sync effect above does not re-run and the typed numbers stay in the
      // inputs for a retry.
      setSaveError(
        getApiErrorMessage(
          err,
          'Torque profile was not saved. Check your connection and try again.'
        )
      );
      return; // no ✓ Saved, and the parent is not told a profile exists
    }
    setSavedFeedback(true);
    setTimeout(() => setSavedFeedback(false), 2500);
    onProfileSaved?.();
  };

  const canSave =
    isEditable &&
    localValues.torqueOptimum !== null &&
    localValues.torqueMin !== null &&
    localValues.torqueMax !== null;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={S.section}>

      {/* Section header. The badge is the resolver's own sourceLabel — the same
          string the call sheet prints in its SOURCE column. */}
      <div style={S.sectionHeader}>
        <p style={S.sectionTitle}>Makeup Torque</p>
        {status === 'loading' && <span style={S.badge('loading')}>Looking up…</span>}
        {status === 'error' && <span style={S.badge('error')}>Lookup failed — enter manually</span>}
        {status === 'resolved' && sourceLabel && (
          <span style={S.badge(isCompanyValue ? 'recalled' : 'spec')}>{sourceLabel}</span>
        )}
        {isMiss && <span style={S.badge('empty')}>Enter torque values</span>}
        {torque?.fromOfflineFallback && (
          <span style={S.badge('empty')}>Offline — library value</span>
        )}
      </div>

      {/* Offline. The local spec arrays answered, which cannot know about a
          company override saved elsewhere. Say so rather than implying this is
          the resolver's answer. */}
      {torque?.fromOfflineFallback && (
        <div style={S.infoRow}>
          <span>📡</span>
          <span>
            No connection — showing the API RP 5C1 library value stored on this device. If your
            company has saved its own torque for this pipe, it will appear once you are back online.
            Saving and reverting need a connection.
          </span>
        </div>
      )}

      {/* A library value, read-only, with a way to take it over. */}
      {isLibraryValue && !isOverriding && !torque?.fromOfflineFallback && (
        <div style={S.infoRow}>
          <span>⚡</span>
          <span>
            Torque is the published <strong>API RP 5C1</strong> value for this pipe.{' '}
            <button type="button" style={S.linkBtn} onClick={beginOverride}>
              Override for my company
            </button>
          </span>
        </div>
      )}

      {/* Overriding. The warning differs by what is being replaced: taking over
          a PUBLISHED figure is the consequential one and says so. Editing a
          value the company already owns is ordinary. Saying "you are replacing
          an API RP 5C1 value" over an existing company value would be false. */}
      {isOverriding && (
        <div style={S.warnRow}>
          <span aria-hidden="true">⚠</span>
          {isLibraryValue ? (
            <span>
              <strong>You are replacing an API RP 5C1 value.</strong> What you save applies to{' '}
              <strong>your company only</strong> and will be used on this screen and on the call
              sheet in place of the published figure — for every future job with this exact pipe.
              The published optimum for reference is{' '}
              <strong>{fmt(torque?.torqueOptimum ?? null) || '—'} ft-lb</strong>.{' '}
              <button type="button" style={S.linkBtn} onClick={handleCancelOverride}>
                Cancel
              </button>
            </span>
          ) : (
            <span>
              <strong>Editing your company's saved value.</strong> It applies to your company only,
              on this screen and on the call sheet, for every future job with this exact pipe.{' '}
              <button type="button" style={S.linkBtn} onClick={handleCancelOverride}>
                Cancel
              </button>
            </span>
          )}
        </div>
      )}

      {/* A company value is displayed. Offer both ways out: change it, or drop
          it and go back to the published figure. Without the first, the only
          route to editing your own number was to delete it and start again. */}
      {canRevert && !isOverriding && (
        <div style={S.infoRow}>
          <span>🏷</span>
          <span>
            This is your company's saved value, not the API RP 5C1 figure.{' '}
            <button type="button" style={S.linkBtn} onClick={beginOverride}>
              Edit it
            </button>
            {' · '}
            <button
              type="button"
              style={S.linkBtn}
              disabled={reverting}
              onClick={handleRevert}
            >
              {reverting ? 'Reverting…' : 'Revert to API RP 5C1'}
            </button>
          </span>
        </div>
      )}

      {/* STC grade/connection compatibility warning */}
      {connectionType === 'STC' &&
        (['N-80', 'L-80', '13Cr L-80', 'C-90', 'T-95', 'P-110', 'Q-125'] as string[]).includes(grade) &&
        (['4-1/2"', '5"', '5-1/2"', '6-5/8"', '7"'] as string[]).includes(size) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 12px',
          background: 'rgba(234,179,8,0.12)',
          border: '1px solid rgba(234,179,8,0.3)',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#eab308',
          marginBottom: '12px',
        }}>
          <span>⚠️</span>
          <span>
            API RP 5C1 does not specify STC torque for {grade} in this size.
            STC is not recommended for this grade — consider LTC or BTC.
            You can enter manufacturer values manually below.
          </span>
        </div>
      )}

      {/* The resolver found nothing: neither the library nor this company holds
          a value for this pipe. Fields are already open — this says why, and
          that the entry is worth making because it will be remembered.

          NOT gated on connection type any more. The old version showed this
          only for non-LTC/STC/BTC, because a standard connection could not
          reach an empty state — it was read-only and simply dead-ended. That
          gate is exactly what stopped TNT entering their LT&C weight. */}
      {isMiss && (
        <div style={{ ...S.infoRow, background: 'rgba(59,130,246,0.12)', borderColor: 'rgba(59,130,246,0.3)', color: '#2563eb' }}>
          <span>💡</span>
          <span>
            API RP 5C1 does not list this size, weight, grade and connection, and your company has
            no saved value for it. Enter the manufacturer's figures below — they'll be remembered
            for every future job with this exact pipe.
          </span>
        </div>
      )}

      {/* Torque fields — read-only cards for spec, inputs for premium */}
      {status !== 'idle' && (
        <>
          <div style={S.torqueGrid}>
            {(['torqueMin', 'torqueOptimum', 'torqueMax'] as const).map((field) => {
              const labels = { torqueMin: 'Minimum', torqueOptimum: 'Optimum', torqueMax: 'Maximum' };
              return (
                <div key={field} style={S.fieldGroup}>
                  <label style={S.label}>{labels[field]}</label>
                  {!isEditable ? (
                    <div style={S.readonlyCard}>
                      <span style={field === 'torqueOptimum' ? S.readonlyValueOpt : S.readonlyValue}>
                        {fmt(localValues[field]) || '—'}
                      </span>
                      <span style={S.readonlyUnit}>ft-lb</span>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        style={S.input()}
                        value={localValues[field] ?? ''}
                        onChange={(e) => handleLocalChange(field, e.target.value)}
                        placeholder="ft-lb"
                        min={0}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* "Shouldered connection" — the combo's own flag, offered whenever the
              fields are open. Not inferred from the thread name beyond the
              default: 'T-BLUE' and 'VAMTOP HC' are the tenant's to tick. */}
          {isEditable && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'hsl(var(--foreground))', marginBottom: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={shoulderedChoice}
                onChange={(e) => { setShoulderedChoice(e.target.checked); setSaveError(null); }}
              />
              Shouldered connection
            </label>
          )}
          {dropsSavedShoulders && (
            <div style={S.warnRow}>
              <span aria-hidden="true">⚠</span>
              <span>Saving removes the low and high shoulder torques on file for this pipe.</span>
            </div>
          )}

          {/* Shoulder torques — for a combo flagged shouldered */}
          {showShoulders && (
            <>
              <div style={S.divider} />
              <div style={{ marginBottom: '8px' }}>
                <label style={S.label}>Shoulder Torques</label>
              </div>
              <div style={S.shoulderGrid}>
                {(['torqueLowShoulder', 'torqueHighShoulder'] as const).map((field) => {
                  const labels = { torqueLowShoulder: 'Low Shoulder', torqueHighShoulder: 'High Shoulder' };
                  return (
                    <div key={field} style={S.fieldGroup}>
                      <label style={S.label}>{labels[field]}</label>
                      {!isEditable ? (
                        <div style={S.readonlyCard}>
                          <span style={S.readonlyValue}>{fmt(localValues[field]) || '—'}</span>
                          <span style={S.readonlyUnit}>ft-lb</span>
                        </div>
                      ) : (
                        <input
                          type="number"
                          style={S.input()}
                          value={localValues[field] ?? ''}
                          onChange={(e) => handleLocalChange(field, e.target.value)}
                          placeholder="ft-lb"
                          min={0}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Save. Offered whenever the fields are open — a miss, or an
              override in progress — regardless of connection type. */}
          {isEditable && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
              <button
                type="button"
                style={S.saveBtn(canSave && !savedFeedback)}
                disabled={!canSave}
                onClick={handleSave}
              >
                {savedFeedback
                  ? '✓ Saved'
                  : isOverriding
                  ? 'Save Company Override'
                  : isCompanyValue
                  ? 'Update Saved Profile'
                  : 'Save Torque Profile'}
              </button>
              {canSave && !isCompanyValue && !saveError && (
                <span style={S.hint}>
                  Will auto-fill for future jobs with this exact pipe combination.
                </span>
              )}
            </div>
          )}

          {/* Server rejected the save. The values above are still the user's —
              nothing was cleared, so "Save Torque Profile" retries as typed. */}
          {saveError && (
            <div style={S.errorRow} role="alert">
              <span aria-hidden="true">⚠</span>
              <span>
                <strong>Not saved.</strong> {saveError} Your values are still here — fix
                the problem and save again.
              </span>
            </div>
          )}

          {/* Spec tolerance note — only about a library value. A company value
              is whatever the tenant saved and carries no ±25% claim. */}
          {isLibraryValue && !isOverriding && (
            <p style={{ ...S.hint, marginTop: '10px' }}>
              ±25% tolerance per API RP 5C1. Min and Max shown reflect the allowable range.
            </p>
          )}
        </>
      )}

      {status === 'idle' && (
        <p style={S.hint}>Select casing size, weight, grade, and connection type to load torque values.</p>
      )}
    </div>
  );
};

export default PipeTorqueFields;

// ─── Integration example ─────────────────────────────────────────────────────
//
// In your pipe profile / job string form:
//
// import { PipeTorqueFields, type TorqueFieldValues } from './PipeTorqueFields';
//
// const [form, setForm] = useState({
//   casingSize: '',
//   casingWeight: null,
//   casingGrade: '',
//   connectionType: '',
//   // torque fields — managed by PipeTorqueFields:
//   torqueMin: null,
//   torqueOptimum: null,
//   torqueMax: null,
//   torqueLowShoulder: null,
//   torqueHighShoulder: null,
//   torqueSource: '',
// });
//
// <PipeTorqueFields
//   size={form.casingSize}
//   weightPpf={form.casingWeight}
//   grade={form.casingGrade}
//   connectionType={form.connectionType}
//   onChange={(torqueFields: TorqueFieldValues) =>
//     setForm(prev => ({ ...prev, ...torqueFields }))
//   }
// />
